import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ErrorPageContextProviderValues, RouteError } from './types';
import { useTranslationAPI } from '../../translations';
import errorCodes from '../../../data/translations/errorCodes';
import { createProvider } from '../../../utils/createProvider';
import { GenericErrorResponse } from '../../app';
import { NetworkError } from '@packages/dataFetch';
import { useHistory } from '@packages/router-dom';
import { PUBLIC_CUSTOM_PAGE_PATH } from '../../../utils/const';
import { useCallbackRef } from '../../../hooks/useCallbackRef';

const [Provider, useErrorPageContext] = createProvider<ErrorPageContextProviderValues>({
	contextName: 'ErrorPageContext',
});

const CustomPageErrorProvider = ({ children }) => {
	const { translate } = useTranslationAPI();
	const [routeError, setRouteError] = useState<RouteError | null>(null);

	const handleResponseByStatus = useMemo(
		() => ({
			403: ({ response }: NetworkError<GenericErrorResponse>) => {
				setRouteError({
					statusCode: 403,
					title: response?.data?.message || translate(errorCodes.forbidden),
					description: response?.data?.description || translate(errorCodes.forbiddenDescription),
				});
			},
			404: ({ response }: NetworkError<GenericErrorResponse>) => {
				setRouteError({
					statusCode: 404,
					title: response?.data?.message || translate(errorCodes.pageNotFound),
					description: response?.data?.description || translate(errorCodes.pageNotFoundDescription),
				});
			},
			400: ({ response }: NetworkError<GenericErrorResponse>) => {
				setRouteError({
					statusCode: 400,
					title: response?.data?.message || translate(errorCodes.badRequest),
					description: response?.data?.description || translate(errorCodes.badRequestDescription),
				});
			},
			410: ({ response }: NetworkError<GenericErrorResponse>) => {
				setRouteError({
					statusCode: 410,
					title: response?.data?.message || translate(errorCodes.deepLinkNotAvailable),
					description: response?.data?.description || translate(errorCodes.deepLinkNotAvailableMessage),
				});
			},
			500: ({ response }: NetworkError<GenericErrorResponse>) => {
				setRouteError({
					statusCode: 500,
					title: response?.data?.message || translate(errorCodes.internalError),
					description: response?.data?.description || translate(errorCodes.errorMessage),
				});
			},
		}),
		[translate],
	);

	const setModuleResponseError = useCallback(
		(error: NetworkError | null) => {
			if (error === null) {
				return setRouteError(null);
			}

			if (typeof error.response !== 'undefined') {
				const { status } = error.response;

				return handleResponseByStatus.hasOwnProperty(status) && handleResponseByStatus[status](error);
			}
		},
		[handleResponseByStatus],
	);

	const providerValue = useMemo(() => {
		return {
			routeError,
			setRouteError,
			setModuleResponseError,
		};
	}, [routeError, setModuleResponseError]);

	const { replace } = useHistory();

	const replaceRef = useCallbackRef(replace);

	useEffect(() => {
		if (routeError) {
			replaceRef(PUBLIC_CUSTOM_PAGE_PATH.error);
		}
	}, [replaceRef, routeError]);

	return <Provider value={providerValue}>{children}</Provider>;
};

export { CustomPageErrorProvider, useErrorPageContext };
